import { Lightbulb } from "lucide-react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from "recharts";
import { useTranslation } from "react-i18next";

const BehavioralSection = ({ report }) => {
   const { t } = useTranslation();
  // Defensive checks
  const behaviouralData = report?.behavioural_ai || {};

  // Transform hourly_activity data into chart-friendly array
  const allHours = Array.from({ length: 24 }, (_, i) => ({
    hour: `${String(i).padStart(2, "0")}:00`,
    activity: 0,
  }));

  const activityMap = behaviouralData.data_for_graph?.hourly_activity || {};
  const hourlyActivity = allHours.map((h) => ({
    ...h,
    activity: activityMap[h.hour.split(":")[0]] || 0,
  }));

  const studyChronotype = behaviouralData.study_chronotype || "N/A";
  const focusPattern = behaviouralData.focus_pattern || "N/A";
  const consistencyLevel = behaviouralData.consistency_level || "N/A";
  const productivityAssessment = behaviouralData.productivity_assessment || "N/A";
  const optimalStudyTimes = behaviouralData.optimal_study_times || [];
  const studyRecommendations = behaviouralData.study_recommendations || [];
  const summaryInsight = behaviouralData.summary_insight ||  t("behavioral.noInsightsAvailable");

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
      <h3 className="text-lg font-bold mb-4">{t("behavioral.behavioralAnalysis")}</h3>
      <p className="text-sm text-slate-600 mb-6">{summaryInsight}</p>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left: Profile Metrics */}
        <div className="space-y-4 bg-slate-50 p-4 rounded-lg border border-slate-200">
          <h4 className="font-semibold text-slate-700 mb-2">{t("behavioral.profileMetrics")}</h4>
          <div>
            <h5 className="font-semibold mb-1">{t("behavioral.studyChronotype")}</h5>
            <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded-full text-sm capitalize">
              {studyChronotype.replace("_", " ")}
            </span>
          </div>
          <div>
            <h5 className="font-semibold mb-1">{t("behavioral.focusPattern")}</h5>
            <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm capitalize">
              {focusPattern.replace("_", " ")}
            </span>
          </div>
          <div>
            <h5 className="font-semibold mb-1">{t("behavioral.consistencyLevel")}</h5>
            <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">
              {consistencyLevel.replace("_", " ")}
            </span>
          </div>
          <div>
            <h5 className="font-semibold mb-1">{t("behavioral.productivityAssessment")}</h5>
            <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm">
              {productivityAssessment}
            </span>
          </div>
        </div>

        {/* Right: Study Times + Graph */}
        <div className="space-y-4 bg-slate-50 p-4 rounded-lg border border-slate-200">
          <h4 className="font-semibold text-slate-700 mb-2"> {t("behavioral.optimalStudyTimes")}</h4>
          {optimalStudyTimes.length > 0 ? (
            <div className="space-y-1 mb-4">
              {optimalStudyTimes.map((time, index) => (
                <div
                  key={index}
                  className="bg-green-50 border border-green-200 p-2 rounded text-sm"
                >
                  {time || "N/A"}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500 mb-4">{t("behavioral.noStudyTimesAvailable")}</p>
          )}

          <h4 className="font-semibold text-slate-700 mb-2">{t("behavioral.hourlyActivity")}</h4>
          {hourlyActivity.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={hourlyActivity}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="activity" stroke="#10b981" strokeWidth={2} dot />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-slate-500"> {t("behavioral.noActivityData")}</p>
          )}
        </div>
      </div>

      {/* Bottom: Recommendations */}
      <div className="mt-6 bg-slate-50 p-4 rounded-lg border border-slate-200">
        <h4 className="font-semibold mb-2">{t("behavioral.recommendations")}</h4>
        {studyRecommendations.length > 0 ? (
          <ul className="space-y-1">
            {studyRecommendations.map((rec, index) => (
              <li key={index} className="flex items-start space-x-2">
                <Lightbulb className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-slate-700">{rec || "N/A"}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500"> {t("behavioral.noRecommendations")}</p>
        )}
      </div>
    </div>
  );
};

export default BehavioralSection;
